<template>
  <v-dialog v-model="show" scrollable v-bind="_attributes">
    <v-card>
      <Toolbar
        title="Dokumentum módosítása"
        btn-title="Módosítás"
        @btnClicked="updateDocument"
        @close="reset"
      >
        <template #extraButtons>
          <v-btn
            text
            @click="cloneAndArchiveDocument"
            :disabled="document.has_child"
          >
            Új dokumentum változat
          </v-btn>
        </template>
      </Toolbar>

      <v-card-text class="pt-3">
        <v-form>
          <v-container>
            <v-row>
              <v-col cols="12" md="6">
                <v-file-input
                  :label="document.file_name && !document.file ? shortyfy(document.file_name):'Feltöltendő Dokumentum'"
                  v-model="document.file"
                  :error-messages="getErrorMessage('file')"
                  accept=".png,image/png,.jpg,image/jpeg,.pdf, application/pdf,.docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  tabindex="1"
                  clearable
                ></v-file-input>
              </v-col>

              <v-col cols="12" md="6">
                <v-file-input
                  label="Kapcsolodó fájlok"
                  multiple
                  v-model="attachedFiles"
                  @change="addAttachments"
                  tabindex="2"
                  accept=".png,image/png,.jpg,image/jpeg,.pdf, application/pdf,.docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                >
                </v-file-input>
              </v-col>
              <v-col v-if="_allAttached.length > 0" cols="12">
                Csatolt fileok
              </v-col>
              <v-col
                sm="4"
                cols="12"
                v-if="_allAttached.length > 0"
                v-for="(file, index) in _allAttached"
                :key="index"
              >
                <v-card>
                  <v-toolbar :color="(file instanceof File) ? '#ddfada' :''">
                    <v-tooltip top>
                      <template #activator="{ on, attrs }">
                        <v-toolbar-title v-on="on" v-bind="attrs" style="width: 80%">
                          {{ (file instanceof File) ? file.name : file.file_name }}
                        </v-toolbar-title>
                      </template>
                      {{ (file instanceof File) ? file.name : file.file_name }}
                    </v-tooltip>

                    <v-tooltip top >
                      <template #activator="{ on, attrs }">
                        <v-btn icon color="red" v-on="on"
                               v-bind="attrs"
                               @click="(file instanceof File) ? removeNewAttachment(file) : removeAttachment(file)"
                        >
                          <v-icon>mdi-trash-can</v-icon>
                        </v-btn>
                      </template>
                      {{ (file instanceof File) ? 'Feltöltött file eltávolítása' : 'Végleges törlés (Nem visszakereshető)' }}
                    </v-tooltip>
                  </v-toolbar>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>

              <v-col cols="12" class="my-0 py-0 text--secondary">
                <small v-if="latestDocument.title">
                  A{{ latestDocument.issuer_name.match(/^[^qwrtzpsdfghjklyxcvbnm]/gi) ? 'z' : '' }}
                  {{ latestDocument.issuer_name }} legutóbbi dokumentuma:
                  <b>{{ latestDocument.serial_number }}</b> {{ latestDocument.title }}
                </small>
                <small v-else>&nbsp;</small>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  label="Dokumentum címe"
                  clearable
                  v-model="document.title"
                  :error-messages="getErrorMessage('title')"
                  @click:append="document.title = null"
                  tabindex="3"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="Rövidítés"
                  clearable
                  v-model="document.abridgment"
                  :error-messages="getErrorMessage('abridgment')"
                  @click:append="document.abridgment = null"
                  tabindex="4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <YearPicker
                  label="Évszám"
                  v-model="document.year"
                  :attributes="inputAttributes"
                  :error-messages="getErrorMessage('year')"
                  :tabindex="5"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="Sorszám"
                  clearable
                  v-model="document.serial_number"
                  :error-messages="getErrorMessage('serial_number')"
                  @click:append="document.serial_number = null"
                  tabindex="6"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-combobox
                  label="Kibocsátó"
                  clearable
                  :items="_.map(issuers,'name')"
                  v-model="document.issuer_name"
                  :error-messages="getErrorMessage('issuer_name')"
                  @click:append="document.issuer_name = null"
                  @change="getActiveRegulators"
                  tabindex="7"
                ></v-combobox>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  label="Szabályzó típus"
                  :items="activeRegulators"
                  v-model="document.regulator"
                  item-text="name"
                  item-id="id"
                  clearable
                  return-object
                  :error-messages="getErrorMessage('regulator')"
                  @click:append="document.regulator = null"
                  tabindex="8"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <Datepicker
                  label="Hatályba lépés"
                  v-model="document.effective_date"
                  :attributes="inputAttributes"
                  :error-messages="getErrorMessage('effective_date')"
                  :tabindex="9"
                />
              </v-col>
              <v-col cols="12" md="4" v-if="!document.valid_until_revoked">
                <Datepicker
                  label="Hatályon kívül helyezés"
                  v-model="document.date_of_repeal"
                  :attributes="inputAttributes"
                  :error-messages="getErrorMessage('date_of_repeal')"
                  :tabindex="10"
                />
              </v-col>
              <v-col cols="12" v-if="document.is_incognito">
                <v-textarea
                  label="Megjegyzés"
                  v-model="document.comment"
                  outlined
                  :attributes="inputAttributes"
                  :error-messages="getErrorMessage('comment')"
                  tabindex="11"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="document.is_public"
                  label="Publikus"
                  tabindex="12"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="document.is_incognito"
                  label="Inkognitó"
                  tabindex="13"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="document.is_archived"
                  label="Archív"
                  tabindex="14"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="document.valid_until_revoked"
                  label="Visszavonásig hatályos"
                  tabindex="15"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="document.is_internal"
                  label="Csak belső hálózatról elérhető"
                  tabindex="16"
                ></v-switch>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  label="Dokumentum előzmény csatolása"
                  v-model="document.parent_id"
                  :error-messages="getErrorMessage('parent_id')"
                  :items="allDocuments"
                  :item-text="getLabel"
                  item-value="id"
                  clearable
                ></v-autocomplete>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <h1>Meta Adatok</h1>
              </v-col>
              <v-col cols="12" md="4" v-for="(meta, index) in metas" :key="meta.slug">
                <v-text-field
                  :label="meta.name"
                  v-model="document.metas[meta.slug]"
                  @click:append="document.metas[meta.slug] = null"
                  clearable
                  :tabindex="20+index"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Datepicker from '../../Pickers/Datepicker';
import YearPicker from '../../Pickers/YearPicker';
import Toolbar from '../../Modals/Toolbar'
import _ from "lodash";

export default {
  name: 'EditDocumentModal',
  components: { Datepicker, YearPicker, Toolbar },
  data () {
    return {
      activeRegulators: [],
      attachedFiles: [],
      inputAttributes: {
        clearable: true,
      },
    }
  },
  computed: {
    ...mapGetters('Modal', { show: 'showEditModal' }),
    ...mapGetters('Document', ['document','allDocuments','issuers','latestDocument']),
    ...mapGetters('Meta', ['metas']),
    ...mapGetters('Regulator', ['regulators']),
    ...mapGetters(['getErrorMessage']),
    _ () {
      return _;
    },
    is_archived(){
      return this.document.is_archived;
    },
    File () {
      return File;
    },
    _allAttached(){
      if (this.document.files && this.document.attached_files) {
        return this.document.files.concat(this.document.attached_files);
      }
      return [];
    },
    _attributes () {
      return this.$vuetify.breakpoint.mobile
        ? {
          fullscreen: true,
          hideOverlay: true,
          transition: 'dialog-bottom-transition',
        }
        : {
          width: '50%',
          persistent: true,
        }
    },
  },
  methods: {
    ...mapActions('Document', ['reset', 'updateDocument','getAllDocuments','cloneAndArchiveDocument', 'deleteAttachmentById','getLatestDocument']),
    getActiveRegulators () {
      if(this.document.issuer_name){
        this.getLatestDocument(this.document.issuer_name);
      }

      let issuer = _.find(this.issuers, (item) => {
        return this.document.issuer_name === item.name
      })
      if (issuer && issuer.regulators.length > 0) {
        this.activeRegulators = issuer.regulators
      }
      else {
        this.activeRegulators = this.regulators
      }
    },
    removeDocumentFile (index) {
      const files = [...this.document.files]

      this.document.files = files.reduce((prev, el, idx) => {
        if (index !== idx) {
          return prev.concat(el)
        }
        return prev
      }, [])
    },
    addAttachments(){
      this.$set(this.document, 'files', this.document.files.concat(this.attachedFiles))
      this.attachedFiles = [];
    },
    removeAttachment (file) {
      this.deleteAttachmentById({ id: file.id, document: this.document })
    },
    removeNewAttachment (file) {
      this.$set(this.document, 'files',
        this.document.files.filter(item => item.name !== file.name && item.size !== file.size))
    },
    shortyfy (string, length = 20) {
      return string.substr(0, Math.floor(length / 2)) + '...' + string.substr(string.length - Math.floor(length / 2))
    },
    getLabel (item) {
      return `${item.serial_number} - ${item.title}`
    },
  },
  watch: {
    show: function () {
      if (this.show) {
        this.getActiveRegulators();
        this.getAllDocuments();
      }
    },
  },
}
</script>
